import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { Checkbox } from "@mui/material";
import { SimpleTreeView } from "@mui/x-tree-view";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { useFormikContext } from "formik";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import { ProductFormData } from "@/components/screens/ProductFormPage/types";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import ICategory from "@/interfaces/product.category.interfaces";
import { ProductFormMessages } from "@/messages/products/product-form.messages";

type Props = {
  categories: ICategory[] | undefined;
  categoriesArray: number[];
  messages: ProductFormMessages;
  onSelectionChange?: (categories: number[]) => void;
};

const CategoryTree: React.FC<Props> = ({ categories, categoriesArray, messages, onSelectionChange }) => {
  const { lang } = useLanguage();
  const router = useNavigate();

  const { values } = useFormikContext<ProductFormData>();

  const handleCategorySelect = (categoryId: number, isChecked: boolean) => {
    const newSelection = isChecked
      ? [...values.selectedCategories, categoryId]
      : values.selectedCategories.filter((id) => id !== categoryId);

    onSelectionChange?.(newSelection);
  };

  const renderTree = (parentNode: ICategory | null, allCategories: ICategory[]) => {
    const parentCode = parentNode ? String(parentNode.code ?? "") : "";
    const childrenCategories = allCategories.filter((cat) => {
      // Garante comparação consistente entre string/number/null
      const catParentCode = String(cat.parentCode ?? "");
      return catParentCode === parentCode;
    });

    return childrenCategories.map((category) => (
      <TreeItem
        itemId={category.id.toString()}
        label={
          <MDBox display="flex" alignItems="center" sx={{ cursor: "pointer" }} gap={1}>
            <Checkbox
              checked={values.selectedCategories.includes(category.id)}
              onChange={(event) => {
                event.stopPropagation();
                const isChecked = event.target.checked;
                handleCategorySelect(category.id, isChecked);
              }}
              onClick={(event) => event.stopPropagation()}
            />
            <MDTypography
              variant="body2"
              color="text"
              fontWeight={"regular"}
            >{`${category.description}`}</MDTypography>
          </MDBox>
        }
        key={category.id}
        slots={{ expandIcon: ArrowRightIcon, collapseIcon: ArrowDropDownIcon }}
      >
        {renderTree(category, allCategories)}
      </TreeItem>
    ));
  };

  // Todos os nós expandidos por padrão na primeira renderização
  const expandedItemIds = React.useMemo(() => {
    if (!categories || categories.length === 0) return [];
    if (!categoriesArray || categoriesArray.length === 0) return [];

    const byId = new Map(categories.map((c) => [c.id, c]));
    const byCode = new Map(categories.map((c) => [c.code, c]));

    const expanded = new Set<string>();

    categoriesArray.forEach((selectedId) => {
      let current = byId.get(selectedId);

      while (current && current.parentCode) {
        const parent = byCode.get(current.parentCode);
        if (!parent) break;
        expanded.add(parent.id.toString());
        current = parent;
      }
    });

    return Array.from(expanded);
  }, [categories, categoriesArray]);

  return (
    <SimpleTreeView
      defaultExpandedItems={expandedItemIds}
      sx={
        categories && categories.length <= 0
          ? {
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              height: "100%",
            }
          : undefined
      }
    >
      {categories && categories.length > 0 ? (
        renderTree(null, categories)
      ) : (
        <>
          <MDTypography variant="body2" color="text" fontWeight={"regular"}>
            {messages.noCategoriesMessage}
          </MDTypography>
          <MDButton
            color="dark"
            variant="text"
            sx={{ marginLeft: -2.4 }}
            onClick={() => router(ROUTE_URLS.createProductCategory(lang))}
          >
            {messages.createCategoryButton}
          </MDButton>
        </>
      )}
    </SimpleTreeView>
  );
};

export default CategoryTree;
